import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { setToken, clearToken, getToken } from '../utils/auth';
import { apiFetch, API_URL } from '../utils/api';
import {
  setAgentAvailable,
  setAgentOffline,
  setupAgentDisconnect,
  updateAgentLastSeen,
} from '../lib/agentPresence';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      const result = await response.json();
      setUser(result.user);
      setTokenState(result.token);
      
      // Store token for Bearer authentication
      if (result.token) {
        setToken(result.token);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        body: JSON.stringify({ name, email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Registration failed');
      }

      const data = await response.json();
      setUser(data.user);
      if (data.token) {
        setTokenState(data.token);
        setToken(data.token);
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    const currentUser = user;
    try {
      if (currentUser?.role === 'telecaller') {
        await setAgentOffline(currentUser);
      }
      await apiFetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
      });
      setUser(null);
      setTokenState(null);
      clearToken();
    } catch (error) {
      console.error('Logout error:', error);
      if (currentUser?.role === 'telecaller') {
        await setAgentOffline(currentUser).catch(() => undefined);
      }
      // Even if logout fails, clear user state
      setUser(null);
      setTokenState(null);
      clearToken();
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async (): Promise<void> => {
    const storedToken = getToken();
    if (!storedToken) {
      setUser(null);
      setTokenState(null);
      return;
    }

    // Set token in state so apiFetch can use it
    setTokenState(storedToken);

    try {
      const response = await apiFetch(`${API_URL}/api/auth/me`);

      if (response.ok) {
        const data = await response.json();
        setUser(data.user || data.me);
      } else if (response.status === 401) {
        // Token is invalid or expired
        setUser(null);
        setTokenState(null);
        clearToken();
      } else {
        // Other errors - don't clear token, might be network issue
        console.warn('Auth check returned non-401 error:', response.status);
      }
    } catch (error) {
      console.error('Refresh user error:', error);
      // Don't clear token on network errors - let user retry
    }
  };

  // Check authentication status on mount - read token from localStorage
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = getToken();

      if (!storedToken) {
        // No token at all - just finish loading, user stays null
        setIsLoading(false);
        return;
      }

      // Have token - validate it with the server
      try {
        await refreshUser();
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    const presenceUser = {
      email: user.email,
      name: user.name,
      role: user.role,
    };

    setupAgentDisconnect(presenceUser).catch((error) => {
      console.warn('Failed to setup Firebase disconnect presence:', error);
    });
    setAgentAvailable(presenceUser).catch((error) => {
      console.warn('Failed to mark agent available:', error);
    });

    const interval = window.setInterval(() => {
      if (!cancelled) {
        updateAgentLastSeen(presenceUser).catch(() => undefined);
      }
    }, 60_000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      setAgentOffline(presenceUser).catch(() => undefined);
    };
  }, [user]);

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
